if (!customElements.get('flash-sale-products')) {
  class FlashSaleProducts extends HTMLElement {
    connectedCallback() {
      this.interval = null;
      this.schedule = this.parseSchedule();
      this.countdownElements = {
        days: this.querySelector('[data-countdown-days]'),
        hours: this.querySelector('[data-countdown-hours]'),
        minutes: this.querySelector('[data-countdown-minutes]'),
        seconds: this.querySelector('[data-countdown-seconds]')
      };
      this.countdownTextElement = this.querySelector('[data-countdown-text]');

      this.updateSaleState();

      if (this.schedule && (this.isOverlayEnabled() || this.isCountdownEnabled())) {
        this.interval = window.setInterval(() => this.updateSaleState(), 1000);
      }
    }

    disconnectedCallback() {
      if (this.interval) {
        window.clearInterval(this.interval);
      }
    }

    parseSchedule() {
      const timezone = (this.dataset.overlayTimezone || '').trim();
      const timezoneOffset = this.parseTimezoneOffset(timezone);

      if (timezoneOffset === null) {
        return null;
      }

      if (this.dataset.saleScheduleMode === 'weekly') {
        const startDay = Number(this.dataset.weeklyStartDay);
        const endDay = Number(this.dataset.weeklyEndDay);
        const startTime = this.parseTime(this.dataset.weeklyStartTime || '00:00');
        const endTime = this.parseTime(this.dataset.weeklyEndTime || '00:00');

        if (!Number.isInteger(startDay) || startDay < 0 || startDay > 6 || !Number.isInteger(endDay) || endDay < 0 || endDay > 6 || !startTime || !endTime) {
          return null;
        }

        return {
          mode: 'weekly',
          timezoneOffset,
          startDay,
          startTime,
          endDay,
          endTime
        };
      }

      const date = (this.dataset.overlayDate || '').trim();
      const time = (this.dataset.overlayTime || '00:00').trim();

      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !this.parseTime(time)) {
        return null;
      }

      const timestamp = Date.parse(`${date}T${time}:00${timezone}`);
      return Number.isNaN(timestamp) ? null : {
        mode: 'specific',
        deadline: timestamp
      };
    }

    parseTimezoneOffset(timezone) {
      const match = /^([+-])(\d{2}):(\d{2})$/.exec(timezone);

      if (!match) {
        return null;
      }

      const hours = Number(match[2]);
      const minutes = Number(match[3]);

      if (hours > 23 || minutes > 59) {
        return null;
      }

      return (match[1] === '-' ? -1 : 1) * ((hours * 60) + minutes);
    }

    parseTime(time) {
      const match = /^(\d{2}):(\d{2})$/.exec(String(time).trim());

      if (!match) {
        return null;
      }

      const hours = Number(match[1]);
      const minutes = Number(match[2]);

      if (hours > 23 || minutes > 59) {
        return null;
      }

      return {
        hours,
        minutes
      };
    }

    isOverlayEnabled() {
      return this.dataset.overlayEnabled === 'true';
    }

    isCountdownEnabled() {
      return this.dataset.countdownEnabled === 'true';
    }

    updateSaleState() {
      const state = this.getSaleState();

      this.updateOverlay(state);
      this.updateCountdown(state);

      if (state && state.mode === 'specific' && state.isExpired && this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
    }

    getSaleState(now = Date.now()) {
      if (!this.schedule) {
        return null;
      }

      if (this.schedule.mode === 'weekly') {
        const saleWindow = this.getWeeklyWindow(now);
        const isActive = now >= saleWindow.start && now < saleWindow.end;

        return {
          mode: 'weekly',
          now,
          isExpired: !isActive,
          deadline: isActive ? saleWindow.end : saleWindow.nextStart,
          countdownPhase: isActive ? 'end' : 'start'
        };
      }

      return {
        mode: 'specific',
        now,
        isExpired: now >= this.schedule.deadline,
        deadline: this.schedule.deadline
      };
    }

    getWeeklyWindow(now) {
      const dayMs = 24 * 60 * 60 * 1000;
      const offsetMs = this.schedule.timezoneOffset * 60 * 1000;
      const localNow = new Date(now + offsetMs);
      const localYear = localNow.getUTCFullYear();
      const localMonth = localNow.getUTCMonth();
      const localDate = localNow.getUTCDate();
      const localDay = localNow.getUTCDay();
      const daysSinceStart = (localDay - this.schedule.startDay + 7) % 7;
      const startLocalTime = Date.UTC(
        localYear,
        localMonth,
        localDate - daysSinceStart,
        this.schedule.startTime.hours,
        this.schedule.startTime.minutes
      );
      const endDayOffset = (this.schedule.endDay - this.schedule.startDay + 7) % 7;
      const endLocalTime = Date.UTC(
        localYear,
        localMonth,
        localDate - daysSinceStart + endDayOffset,
        this.schedule.endTime.hours,
        this.schedule.endTime.minutes
      );
      const start = startLocalTime - offsetMs;
      let end = endLocalTime - offsetMs;

      if (end <= start) {
        end += 7 * dayMs;
      }

      return {
        start,
        end,
        nextStart: start > now ? start : start + (7 * dayMs)
      };
    }

    updateOverlay(state) {
      if (!this.isOverlayEnabled() || !state) {
        this.classList.remove('is-expired');
        return;
      }

      this.classList.toggle('is-expired', state.isExpired);
    }

    updateCountdown(state) {
      if (!this.isCountdownEnabled()) {
        return;
      }

      this.updateCountdownText(state);

      const remaining = state && state.deadline ? Math.max(state.deadline - state.now, 0) : 0;
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setCountdownValue('days', days);
      this.setCountdownValue('hours', hours);
      this.setCountdownValue('minutes', minutes);
      this.setCountdownValue('seconds', seconds);
    }

    updateCountdownText(state) {
      if (!this.countdownTextElement) {
        return;
      }

      const showStartText = state && state.mode === 'weekly' && state.countdownPhase === 'start';
      const text = showStartText ? this.dataset.countdownStartText : this.dataset.countdownEndText;

      this.countdownTextElement.textContent = text || '';
      this.countdownTextElement.hidden = !text;
    }

    setCountdownValue(key, value) {
      if (this.countdownElements[key]) {
        this.countdownElements[key].textContent = String(value).padStart(2, '0');
      }
    }
  }

  customElements.define('flash-sale-products', FlashSaleProducts);
}
